import { CommonModule } from '@angular/common';
import { Component, OnDestroy, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgSelectModule } from '@ng-select/ng-select';
import { TooltipDirective } from 'ngx-bootstrap/tooltip';
import { PlanType } from '@bn/app-shield/enums';
import { UploadScanningScannerMode } from '@bn/app-shield/openapi';
import { MapShieldUploadScanningChartData, ShieldUploadScanningChartData } from '@bn/app-shield/stores/helpers/shield-api-store.helper';
import { AppRoutes } from '@bn/dashboard-core/enums';
import { DashboardSharedModule } from '@bn/dashboard-shared';
import { SharedEntitiesModule } from '@bn/shared/entities';
import { ContentService } from '@bn/shared/services';
import { StoreRequest } from '@bn/shared/state-store';
import { LazyLoadDirective } from '@bn/web-ui';
import { BadgeComponent } from '@bn/web-ui/badge/badge.component';
import { CardComponent } from '@bn/web-ui/card/card.component';
import { ChartMultiMetricComponent } from '@bn/web-ui/chart/components/charts/chart-multi-metric/chart-multi-metric.component';
import { SelectItem } from '@bn/web-ui/select/models';
import { SwitchComponent } from '@bn/web-ui/switch/switch.component';
import { ShieldApiStore, ShieldUploadScanningApiStore } from '../../stores';

@Component({
  templateUrl: './app-shield-upload-scanning.page.html',
  styleUrls: ['./app-shield-upload-scanning.page.scss'],
  imports: [
    CommonModule,
    FormsModule,
    DashboardSharedModule,
    BadgeComponent,
    CardComponent,
    SwitchComponent,
    ChartMultiMetricComponent,
    FaIconComponent,
    NgSelectModule,
    TooltipDirective,
    LazyLoadDirective,
    SharedEntitiesModule,
  ],
})
export class AppShieldUploadScanningPage implements OnDestroy {
  planType = PlanType;

  #store = inject(ShieldUploadScanningApiStore);
  #shieldStore = inject(ShieldApiStore);
  #contentService = inject(ContentService);

  selectedShield = this.#shieldStore.selectedEntity();
  isLoading = this.#store.isLoading(StoreRequest.Item);
  isUpdating = {
    antivirus: this.#store.isLoading(StoreRequest.Update, ['antivirusScanningMode']),
    csam: this.#store.isLoading(StoreRequest.Update, ['csamScanningMode']),
    antivirusSelect: this.#store.isLoading(StoreRequest.Update, ['antivirusScanningModeSelect']),
    csamSelect: this.#store.isLoading(StoreRequest.Update, ['csamScanningModeSelect']),
    enabled: this.#store.isLoading(StoreRequest.Update, ['isEnabled']),
  };
  isEnabled = this.#store.isEnabled;
  csamMode = this.#store.csamScanningMode;
  antivirusMode = this.#store.antivirusScanningMode;
  shieldId = this.#shieldStore.selectedEntityId();

  loggedRequests = signal(0);
  totalFilesScanned = signal(0);
  blockedRequests = signal(0);
  uploadScanningChartHasData = signal(false);
  uploadScanningChart = signal<ShieldUploadScanningChartData>(MapShieldUploadScanningChartData());

  csamSwitch = computed(() => {
    const mode = this.csamMode?.();

    return mode !== undefined && mode !== UploadScanningScannerMode.Disabled;
  });

  antivirusSwitch = computed(() => {
    const mode = this.antivirusMode?.();
    const hasPaidPlan = (this.selectedShield()?.planType ?? PlanType.Basic) > PlanType.Basic;

    return mode !== undefined && mode !== UploadScanningScannerMode.Disabled && hasPaidPlan;
  });

  antivirusSelect = computed(() => {
    const mode = this.antivirusMode?.();

    return mode !== UploadScanningScannerMode.Block ? UploadScanningScannerMode.Log : UploadScanningScannerMode.Block;
  });

  csamSelect = computed(() => {
    const mode = this.csamMode?.();

    return mode !== UploadScanningScannerMode.Block ? UploadScanningScannerMode.Log : UploadScanningScannerMode.Block;
  });

  uploadScanningAction = UploadScanningScannerMode;
  availableActions: SelectItem[] = [
    {
      Id: UploadScanningScannerMode.Log,
      Name: 'Log',
    },
    {
      Id: UploadScanningScannerMode.Block,
      Name: 'Block',
    },
  ];

  constructor() {
    const pullZoneId = this.#shieldStore.pullZoneId();
    this.#contentService.setTitle('Upload scanning');
    this.#contentService.setBackButton({
      routerLink: `/${AppRoutes.CDN}/${pullZoneId}/${AppRoutes.Shield}`,
    });

    this.subscribeToStoreSignals();
  }

  ngOnDestroy(): void {
    this.#contentService.setBackButton(null);
  }

  onUploadScaningChange(isEnabled: boolean): void {
    const isAdvanced = (this.selectedShield()?.planType ?? PlanType.Basic) > PlanType.Basic;
    const csamScanningMode = isEnabled ? UploadScanningScannerMode.Block : UploadScanningScannerMode.Disabled;
    const antivirusScanningMode = isEnabled && isAdvanced ? UploadScanningScannerMode.Block : UploadScanningScannerMode.Disabled;

    this.#store.updateUploadScanning({
      shieldZoneId: Number(this.shieldId()),
      data: {
        isEnabled,
        csamScanningMode,
        antivirusScanningMode,
      },
      customFields: ['isEnabled'],
    });
  }

  onActionSelect(action: UploadScanningScannerMode, key: 'antivirusScanningMode' | 'csamScanningMode'): void {
    this.#store.updateUploadScanning({
      shieldZoneId: Number(this.shieldId()),
      data: {
        [key]: action,
      },
      customFields: [key + 'Select'],
    });
  }

  onSwitchClick(value: boolean, key: 'antivirusScanningMode' | 'csamScanningMode'): void {
    const currentValues = {
      antivirusScanningMode: this.#store.antivirusScanningMode?.(),
      csamScanningMode: this.#store.csamScanningMode?.(),
      [key]: value,
    };

    // Note: If both values are OFF turn "isEnabled": false, and if at least one is ON turn main "isEnabled": true
    let isEnabled = this.#store.isEnabled?.() ?? false;

    if (!currentValues.antivirusScanningMode && !currentValues.csamScanningMode) isEnabled = false;
    if (currentValues.antivirusScanningMode || currentValues.csamScanningMode) isEnabled = true;

    this.#store.updateUploadScanning({
      shieldZoneId: Number(this.shieldId()),
      data: {
        isEnabled,
        [key]: value ? UploadScanningScannerMode.Block : UploadScanningScannerMode.Disabled,
      },
      customFields: [key],
    });
  }

  private subscribeToStoreSignals(): void {
    toObservable(this.#store.chartMetrics)
      .pipe(takeUntilDestroyed())
      .subscribe((chartMetrics) => {
        if (chartMetrics) {
          const { totalLoggedRequests, totalBlockedRequests, totalFilesScanned } = chartMetrics.total;

          this.uploadScanningChart.set(chartMetrics);
          this.uploadScanningChartHasData.set(chartMetrics.hasData);

          this.loggedRequests.set(totalLoggedRequests);
          this.blockedRequests.set(totalBlockedRequests);
          this.totalFilesScanned.set(totalFilesScanned);
        }
      });
  }
}
